import { ReactNode } from 'react';
import cn from 'classnames';
import { MdAllInclusive, MdImage, MdVideocam, MdViewCarousel } from 'react-icons/md';
import {
  CreativeKind,
  CreativeKindType,
  MetricCard,
  Recommendation
} from './creativeSuite-constants';

const KIND_ICON = {
  [CreativeKind.VIDEO]: MdVideocam,
  [CreativeKind.CAROUSEL]: MdViewCarousel,
  [CreativeKind.PHOTO]: MdImage
};

export const CreativeThumb = ({
  kind,
  small = false
}: {
  kind: CreativeKindType;
  small?: boolean;
}) => {
  const Icon = KIND_ICON[kind];
  return (
    <span className={cn('creative-thumb', { small })}>
      <Icon aria-hidden />
    </span>
  );
};

export const LabelChip = ({ children = 'LOW' }: { children?: ReactNode }) => (
  <span className='label-chip label-low'>{children}</span>
);

const MetricValue = ({ metric }: { metric: MetricCard }) => {
  if (metric.chip === 'low') return <LabelChip>{metric.value}</LabelChip>;
  return (
    <>
      <span className={cn('tone', metric.valueTone)}>{metric.value}</span>
      {metric.valueSuffix && <span className='m-suffix'>{metric.valueSuffix}</span>}
    </>
  );
};

export const MetricGrid = ({ metrics, columns }: { metrics: MetricCard[]; columns: number }) => (
  <div className='bp-metric-grid' style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
    {metrics.map(metric => (
      <div className='bp-metric' key={metric.label}>
        <div className='m-lab'>{metric.label}</div>
        <div className='m-val'>
          <MetricValue metric={metric} />
        </div>
        <div className={cn('m-sub', 'tone', metric.subTone)}>{metric.sub}</div>
      </div>
    ))}
  </div>
);

export const Tag = ({
  variant,
  icon,
  children
}: {
  variant: 'red' | 'amber' | 'green' | 'purple' | 'blue';
  icon?: ReactNode;
  children: ReactNode;
}) => (
  <span className={cn('tag', `tag-${variant}`)}>
    {icon}
    {children}
  </span>
);

export const SrcCue = ({ children = 'Meta API' }: { children?: ReactNode }) => (
  <span className='src-cue'>
    <MdAllInclusive aria-hidden /> {children}
  </span>
);

export const RecBox = ({ rec }: { rec: Recommendation }) => (
  <div className='rec-box'>
    <div className='rl'>{rec.title}</div>
    <div className='rt'>{rec.body}</div>
  </div>
);

// Shared card shell: a white rounded panel with an optional header row.
export const SectionCard = ({
  title,
  sub,
  right,
  className,
  children
}: {
  title?: string;
  sub?: string;
  right?: ReactNode;
  className?: string;
  children: ReactNode;
}) => (
  <div className={cn('bp-card', className)}>
    {(title || right) && (
      <div className='card-head'>
        <div>
          {title && <p className='bp-h'>{title}</p>}
          {sub && <p className='bp-hsub'>{sub}</p>}
        </div>
        {right}
      </div>
    )}
    {children}
  </div>
);
